"use client"
import { QuoteDisplay } from "./components/QuoteDisplay"
import { StatsDisplay } from "./components/StatsDisplay"
import { useStreamingQuote } from "./hooks/useStreamingQuote"
import { useCounterStats } from "./hooks/useCounterStats"
import { ErrorBoundary } from "./components/ErrorBoundary"
import { useState, useEffect } from "react"
import "./App.css"

function App() {
  const { quote, isLoading, error, generateQuote } = useStreamingQuote()
  const { stats, isLoading: statsLoading, hasLoaded, refreshStats } = useCounterStats()
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  const handleCategoryClick = async (category: string) => {
    setSelectedCategory(category)
    await generateQuote(category)
    // Refresh stats after generating quote with a small delay
    setTimeout(() => refreshStats(), 1500)
  }

  const handleGenerateClick = async () => {
    await generateQuote()
    setSelectedCategory(null)
    // Refresh stats after generating quote with a small delay
    setTimeout(() => refreshStats(), 1500)
  }

  return (
    <ErrorBoundary>
      <div className="app-container">
        <div className="background-decoration">
          <div className="floating-orb orb-1"></div>
          <div className="floating-orb orb-2"></div>
          <div className="floating-orb orb-3"></div>
        </div>

        <div className="content-wrapper">
          <header className="app-header">
            <div className="status-badge">
              <div className={`status-dot ${isOnline ? "online" : "offline"}`}></div>
              <span>{isOnline ? "AI-Powered Inspiration" : "Offline Mode"}</span>
            </div>
            <h1 className="app-title">MotivationX</h1>
            <p className="app-subtitle">
              Unlock your potential with AI-generated motivational quotes that inspire action
            </p>

            <StatsDisplay stats={stats} isLoading={statsLoading} hasLoaded={hasLoaded} />
          </header>

          <QuoteDisplay quote={quote} isLoading={isLoading} />

          <div className="action-section">
            <button
              onClick={handleGenerateClick}
              disabled={isLoading}
              className={`generate-btn ${isLoading ? "loading" : ""}`}
            >
              <span className="btn-content">
                {isLoading ? (
                  <>
                    <div className="spinner"></div>
                    <span>Generating Magic...</span>
                  </>
                ) : (
                  <>
                    <span className="lightning-icon">⚡</span>
                    <span>Generate New Quote</span>
                  </>
                )}
              </span>
            </button>

            <div className="quick-actions">
              <button
                className={`category-btn ${selectedCategory === "fitness" ? "active" : ""}`}
                onClick={() => handleCategoryClick("fitness")}
                disabled={isLoading}
              >
                💪 Fitness
              </button>
              <button
                className={`category-btn ${selectedCategory === "business" ? "active" : ""}`}
                onClick={() => handleCategoryClick("business")}
                disabled={isLoading}
              >
                💼 Business
              </button>
              <button
                className={`category-btn ${selectedCategory === "success" ? "active" : ""}`}
                onClick={() => handleCategoryClick("success")}
                disabled={isLoading}
              >
                🎯 Success
              </button>
              <button
                className={`category-btn ${selectedCategory === "golf" ? "active" : ""}`}
                onClick={() => handleCategoryClick("golf")}
                disabled={isLoading}
              >
                ⛳ Golf
              </button>
              <button
                className={`category-btn ${selectedCategory === "eating" ? "active" : ""}`}
                onClick={() => handleCategoryClick("eating")}
                disabled={isLoading}
              >
                🍎 Health
              </button>
              <button
                className={`category-btn ${selectedCategory === "calm" ? "active" : ""}`}
                onClick={() => handleCategoryClick("calm")}
                disabled={isLoading}
              >
                🧘‍♂️ Calm
              </button>
              <button
                className={`category-btn ${selectedCategory === "humble" ? "active" : ""}`}
                onClick={() => handleCategoryClick("humble")}
                disabled={isLoading}
              >
                🙏 Humble
              </button>
              <button
                className={`category-btn ${selectedCategory === "study" ? "active" : ""}`}
                onClick={() => handleCategoryClick("study")}
                disabled={isLoading}
              >
                📚 Study
              </button>
              <button
                className={`category-btn ${selectedCategory === "stress" ? "active" : ""}`}
                onClick={() => handleCategoryClick("stress")}
                disabled={isLoading}
              >
                😰 Stress
              </button>
              <button
                className={`category-btn ${selectedCategory === "relationship" ? "active" : ""}`}
                onClick={() => handleCategoryClick("relationship")}
                disabled={isLoading}
              >
                💕 Relationships
              </button>
            </div>

            {selectedCategory && (
              <div className="app-subtitle-motivational">
                <span>Generating {selectedCategory} motivation...</span>
              </div>
            )}

            {!isOnline && (
              <div className="offline-indicator">
                <span>📱 Offline mode - Using cached quotes</span>
              </div>
            )}
          </div>

          {error && (
            <div className="error-container">
              <div className="error-icon">⚠️</div>
              <p className="error-message">{error}</p>
              <button onClick={handleGenerateClick} className="retry-btn">
                Try Again
              </button>
            </div>
          )}

          <footer className="app-footer">
            <div className="footer-content">
              <span className="check-icon">✓</span>
              <span>Powered by Llama3.2 AI Model</span>
            </div>
          </footer>
        </div>
      </div>
    </ErrorBoundary>
  )
}

export default App
